import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Table, Space, DatePicker, Input, Select, ConfigProvider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import ruRU from 'antd/lib/locale/ru_RU';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import { PredictionActionHistoryItem, ActionType, ActiveModel } from '../../types';

const { RangePicker } = DatePicker;

// Пауза перед запросом после изменения фильтров, мс
const FILTERS_DEBOUNCE_MS = 400;

dayjs.locale('ru');

interface ActionHistoryTableProps {
  history: PredictionActionHistoryItem[];
  loading?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
  onFiltersChange?: (filters: {
    dateFrom?: string;
    dateTo?: string;
    email?: string;
    modelIds?: number[];
    actionTypeId?: number;
  }) => void;
  actionTypes?: ActionType[];
  activeModels?: ActiveModel[];
}

export const ActionHistoryTable: React.FC<ActionHistoryTableProps> = ({
  history,
  loading = false,
  pagination,
  onTableChange,
  onFiltersChange,
  actionTypes = [],
  activeModels = []
}) => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [emailFilter, setEmailFilter] = useState<string>('');
  const [selectedModels, setSelectedModels] = useState<number[]>([]);
  const [selectedActionType, setSelectedActionType] = useState<number | undefined>(undefined);
  const isFirstMount = useRef(true);

  useEffect(() => {
    // Skip the first mount to prevent duplicate API call
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    // Only trigger filter changes when filters actually change (not on initial load).
    // Запрос — через паузу, а не на каждую букву email
    if (!onFiltersChange) return;

    const timer = setTimeout(() => {
      onFiltersChange({
        dateFrom: dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: dateRange?.[1]?.format('YYYY-MM-DD'),
        email: emailFilter || undefined,
        modelIds: selectedModels.length > 0 ? selectedModels : undefined,
        actionTypeId: selectedActionType,
      });
    }, FILTERS_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [dateRange, emailFilter, selectedModels, selectedActionType]);


  // Remove client-side filtering as we now use server-side filtering

  const columns: ColumnsType<PredictionActionHistoryItem> = [
    {
      title: 'Первое выполнение',
      dataIndex: 'action_at',
      key: 'action_at',
      width: 170,
      sorter: true,
    },
    {
      title: 'Последнее выполнение',
      dataIndex: 'last_action_at',
      key: 'last_action_at',
      width: 170,
      sorter: true,
      render: (lastActionAt: string | null, record: PredictionActionHistoryItem) =>
        lastActionAt && lastActionAt !== record.action_at ? lastActionAt : '—',
    },
    {
      title: 'Модель',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 200,
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
      sorter: true,
    },
    {
      title: 'Что сделано',
      dataIndex: 'action_done',
      key: 'action_done',
      width: 200,
    },
    {
      title: 'Условия',
      dataIndex: 'conditions',
      key: 'conditions',
      width: 300,
      ellipsis: true,
    },
  ];

  return (
    <div>
      <Space wrap size="middle" style={{ width: '100%', marginBottom: 16 }}>
        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Дата:</div>
          <ConfigProvider locale={ruRU}>
            <RangePicker
              format="DD.MM.YYYY"
              placeholder={['От', 'До']}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)}
              style={{ width: 260 }}
            />
          </ConfigProvider>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Email:</div>
          <Input
            placeholder="Введите email"
            prefix={<SearchOutlined />}
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            style={{ width: 260 }}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Модель:</div>
          <Select
            mode="multiple"
            placeholder="Выберите модели"
            value={selectedModels}
            onChange={setSelectedModels}
            style={{ width: 260 }}
            allowClear
            maxTagCount="responsive"
            loading={false}
            notFoundContent="Нет данных"
          >
            {activeModels.map(model => (
              <Select.Option key={model.id} value={model.id}>
                {model.name}{model.is_active === false ? ' (выключена)' : ''}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>Тип действия:</div>
          <Select
            placeholder="Выберите тип действия"
            value={selectedActionType}
            onChange={setSelectedActionType}
            style={{ width: 260 }}
            allowClear
          >
            {actionTypes.map(actionType => (
              <Select.Option key={actionType.id} value={actionType.id}>
                {actionType.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Space>

      <Table
        columns={columns}
        dataSource={history}
        // У строк истории нет поля id — ключ собираем из модели, ученика, действия и даты
        rowKey={(record) => `${record.model_name}|${record.email}|${record.action_done}|${record.action_at}`}
        scroll={{ x: 1200 }}
        size="small"
        loading={loading}
        onChange={onTableChange}
        pagination={pagination ? {
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Показано ${range[0]}-${range[1]} из ${total}`,
        } : false}
      />
    </div>
  );
};

